import { useMemo, useState, useCallback, Fragment } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { filtersToExternalQuery, Table, TableView } from '@components/table';
import { useTableStore } from '@components/table/table-store';
import { useEvent } from '@hooks/use-event';
import { toQueryString } from '@utils/requests';

import { api } from 'api';

import { updateApplicantsListSettings, useApplicantsListSettings } from 'api/users';
import { Loading } from 'components/Loading';
import { useStore } from 'models/Provider';
import { ISearchApplication } from 'types/applications';

import { useApplicantsColumns } from './applicants-columns';
import { mapKeyToSortKey, pageSize, useApplicantsFilterState } from './applicants-config';
import { ApplicantsHeader } from './applicants-header';
import { useApplicantsTableFilterConfig } from './applicants-table-filters';

export const Applicants = () => {
  const { t } = useStore();
  const [count, setCount] = useState(0);

  const settingsQuery = useApplicantsListSettings();
  const tableView = settingsQuery.data;
  const client = useQueryClient();

  const handleTableViewChange = useCallback(
    (newTableView: TableView) => {
      client.setQueryData(['applicants-list-settings'], newTableView);
      updateApplicantsListSettings(newTableView);
    },
    [client]
  );

  const tableFilterConfig = useApplicantsTableFilterConfig();
  const search = useApplicantsFilterState((state) => state.debouncedSearch);
  const totalCountQuery = api.applications.useTotalCount('applicants-count');
  const totalCount = totalCountQuery.data || 0;

  const sort = tableView?.sortingState ? tableView.sortingState[0] : null;

  const countWithMaxPageSize = Math.min(count, pageSize);

  const queryString = useMemo(() => {
    const filterParams = filtersToExternalQuery(tableFilterConfig, tableView?.appliedFilters ?? []);

    return toQueryString({
      page_size: pageSize,
      ...filterParams,
      search_multi_match: search || null,
      ordering: sort
        ? `${sort?.desc ? '-' : ''}${
            mapKeyToSortKey[sort.id as keyof ISearchApplication] || sort?.id
          }`
        : '',
    });
  }, [search, sort, tableFilterConfig, tableView]);

  const applicantsQuery = useQuery({
    queryKey: ['applicants', queryString, tableView?.conjunction],
    keepPreviousData: true,
    queryFn: () =>
      tableView?.conjunction === 'or'
        ? api.applications.orSearchList(queryString, 1)
        : api.applications.searchList(queryString, 1),
    onSuccess(data) {
      setCount(data.count);
    },
    enabled: settingsQuery.isSuccess,
  });

  const isLoading =
    applicantsQuery.isLoading || totalCountQuery.isLoading || settingsQuery.isLoading;

  const onPersist = useEvent((tableView: TableView) => {
    handleTableViewChange(tableView);
  });

  const tableStore = useTableStore({
    selectedView: tableView,
    onPersist,
    externalFilters: true,
  });

  const availableColumns = useApplicantsColumns();

  return (
    <Fragment>
      <ApplicantsHeader
        conjunction={tableView?.conjunction}
        count={countWithMaxPageSize}
        totalCount={totalCount}
        query={queryString}
      />
      {isLoading && <Loading />}
      {settingsQuery.isSuccess && applicantsQuery.isSuccess && (
        <Table
          isRefetching={applicantsQuery.isSuccess && applicantsQuery.isFetching}
          hideExport
          availableColumns={availableColumns}
          data={applicantsQuery.data?.results ?? []}
          idColumn="id"
          store={tableStore}
          filterProps={{
            config: tableFilterConfig,
            addFilterText: t('filters.add'),
            showingText: t('applicants.filters.showing', countWithMaxPageSize, totalCount),
          }}
        />
      )}
    </Fragment>
  );
};
